// UserForm.jsx

import React, { useState, useEffect } from 'react';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import Button from '@mui/material/Button';
import { TextField, Typography } from '@mui/material';
import Stack from '@mui/material/Stack';
import { useSnackbar } from '../../../context/SnackbarContext';
import { useTranslation } from 'react-i18next';
import UserTypeSelect from '../../common/controls/UserTypeSelect';
import { useCreateUserInvite } from '../../../hooks/users/useCreateUserInvite';

export default function InviteUserForm({ 
  triggerCallback, 
  userInvitesData = null, 
  userOfficeData = null, 
  userInvite = null, 
  mode = "create", 
  open, 
  onClose 
}) {
  const { t } = useTranslation('common'); 

  const [name, setName] = useState(userInvite ? userInvite.name : "");
  const [email, setEmail] = useState(userInvite ? userInvite.email : "");
  const [accountType, setAccountType] = useState(userInvite ? userInvite.type : "");
  const [saving, setSaving] = useState(false);

  const onSaveUserInviteSuccess = (newRecord) => {
    if (triggerCallback) triggerCallback();
    onClose();
    showSnackbar(t('user_invites.messages.create_success'), "success");
  };

  const { create } = useCreateUserInvite(onSaveUserInviteSuccess);
  const { showSnackbar } = useSnackbar();

  useEffect(() => {
    setName(userInvite ? userInvite.name : "");
    setEmail(userInvite ? userInvite.email : "");
  }, [userInvite, open]);

  // 🔑 Collect all existing emails
  const allEmails = [
    ...(userOfficeData ? userOfficeData.map(uo => uo.user?.email).filter(Boolean) : []),
    ...(userInvitesData ? userInvitesData.map(ui => ui.email).filter(Boolean) : [])
  ];

  const handleSave = async () => {

    if (!name.trim()) {
      showSnackbar(t('user_invites.messages.name_required'), "warning");
      return;
    }
    if (!email.trim()) {
      showSnackbar(t('user_invites.messages.email_required'), "warning");
      return;
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      showSnackbar(t('user_invites.messages.email_invalid'), "warning");
      return;
    }

    // 🚫 Duplicate check
    if (allEmails.includes(email)) {
      showSnackbar(t('user_invites.messages.email_duplicate'), "error");
      return;
    }

    if (!accountType.trim()) {
      showSnackbar(t('user_invites.messages.account_type_required'), "warning");
      return;
    }

    try {
      setSaving(true);
      await create({ 
        name,
        email,
        type: accountType
      });
      setName("");
      setEmail("");
      setAccountType("");
      setSaving(false);
    } catch (err) {
      showSnackbar(t('user_invites.messages.save_fail'), "error");
      setSaving(false);
    }
  };

  const handleCancel = () => {
    setName("");
    setEmail("");
    setAccountType("");
    setSaving(false);
    onClose();
  };

  return (
    <Dialog onClose={handleCancel} open={open} disableRestoreFocus>
      <DialogTitle>Invite User</DialogTitle>

      <DialogContent dividers>
        <Stack spacing={1}>
          <Typography variant='body1'>
            Use this function to invite new users into your clinic/branch. This will send an E-mail to the user with instructions on how to join. 
          </Typography>
          <TextField
            label="Name"
            variant="outlined"
            value={name}
            onChange={(e) => setName(e.target.value)}
            fullWidth
          />
          <TextField
            label="E-mail"
            variant="outlined"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            fullWidth
            disabled={mode === "edit"} // 👈 disables field in edit mode
          />
          <UserTypeSelect 
            label='Account Type'
            value={accountType}
            onChange={(value) => setAccountType(value)}
          />
        </Stack>
      </DialogContent>

      <DialogActions>
        <Button onClick={handleSave} disabled={saving}>Send Invite</Button>
        <Button onClick={handleCancel}>Cancel</Button>
      </DialogActions>
    </Dialog>
  );
}
